import { addDays, format, isAfter, isBefore, parseISO, subDays } from 'date-fns';
import { useMemo, useState } from 'react';
import { TimeSlotEntry } from '../types/booking';
import { extractDateFromISOString } from '../utils/dates';

export const useDateStepper = (selectedDate: string, setSelectedDate: (date: string) => void, timeSlots: TimeSlotEntry[]) => {
	const [year, setYear] = useState(() => parseISO(selectedDate).getFullYear());
	const [month, setMonth] = useState(() => parseISO(selectedDate).getMonth());

	const hasAvailabilityInMonth = (targetMonth: number, targetYear: number) => {
		return timeSlots.some((slot) => {
			const slotDate = new Date(slot.start_datetime);
			return slotDate.getMonth() === targetMonth && slotDate.getFullYear() === targetYear;
		});
	};

	const hasPreviousDayAvailability = useMemo(() => {
		return timeSlots.some((slot) => isBefore(extractDateFromISOString(slot.start_datetime), selectedDate));
	}, [selectedDate, timeSlots]);

	const hasNextDayAvailability = useMemo(() => {
		return timeSlots.some((slot) => isAfter(extractDateFromISOString(slot.start_datetime), selectedDate));
	}, [selectedDate, timeSlots]);

	const hasPreviousMonthAvailability = useMemo(() => {
		const prevMonth = month === 0 ? 11 : month - 1;
		const prevYear = month === 0 ? year - 1 : year;
		return hasAvailabilityInMonth(prevMonth, prevYear);
	}, [month, year, timeSlots]);

	const hasNextMonthAvailability = useMemo(() => {
		const nextMonth = (month + 1) % 12;
		const nextYear = month === 11 ? year + 1 : year;
		return hasAvailabilityInMonth(nextMonth, nextYear);
	}, [month, year, timeSlots]);

	const nextMonth = () => {
		setMonth((prev) => (prev + 1) % 12);
		if (month === 11) {
			setYear((prev) => prev + 1);
		}
	};

	const previousMonth = () => {
		setMonth((prev) => (prev - 1 + 12) % 12);
		if (month === 0) {
			setYear((prev) => prev - 1);
		}
	};

	const nextDay = () => {
		setSelectedDate((prevDate: string) => {
			const date = addDays(parseISO(prevDate), 1);
			return format(date, 'yyyy-MM-dd');
		});
	};

	const previousDay = () => {
		setSelectedDate((prevDate: string) => {
			const date = subDays(parseISO(prevDate), 1);
			return format(date, 'yyyy-MM-dd');
		});
	};

	return {
		hasNextDayAvailability,
		hasNextMonthAvailability,
		hasPreviousDayAvailability,
		hasPreviousMonthAvailability,
		month,
		nextDay,
		nextMonth,
		previousDay,
		previousMonth,
		year,
	};
};
